/**
 * Client-side route alias / redirect registry (capstone: "Route / redirect
 * coverage gaps", docs/qa/DESIGN-PATTERNS.md).
 *
 * PROBLEM this closes: legacy URLs that non-React surfaces still link to —
 * email footers (`/unsubscribe`), the mobile-app menu (`/announcements/*`),
 * legacy jade CTAs (`/mobile`), bare `/` — used to have no React
 * home, and each miss was found one-at-a-time in QA (#353 #354 #355 #356 #430).
 * This file makes "which legacy path has no React home" a single build-time
 * list checked by `frontend/test/routes/redirects.test.ts`, not a QA discovery.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CLIENT vs SERVER split (be honest about what this registry owns)
 * ─────────────────────────────────────────────────────────────────────────
 * Redirect coverage for the migration is split across two owners:
 *
 *  • SERVER-owned (NOT here, do not duplicate): the canonical-legacy-URL →
 *    `/react/<path>` bounce lived in `api/ViewsController` as
 *    `MIGRATED_AUTH_REDIRECTS` / `MIGRATED_AUTH_REDIRECT_PATTERNS`. It is
 *    currently DISABLED pending a per-user rollout (see the memory note
 *    `reference_views_controller_cutover.md`). That table is backend-owned and
 *    out of scope for the frontend; this registry deliberately does not mirror
 *    it — mirroring server behavior on the client would be the exact kind of
 *    scattered, drift-prone duplication the capstone is trying to remove.
 *
 *  • CLIENT-owned (THIS registry): redirects/aliases that live inside the React
 *    bundles themselves — the authenticated data-router (`AuthenticatedApp.tsx`,
 *    basename `/react`) and the public landing router (`unauthenticated/App.tsx`).
 *    Before this file they were scattered inline `<Navigate>` routes and an
 *    inline alias array. They are consolidated below and consumed by both route
 *    tables so adding a legacy alias is one entry here, not a new `<Navigate>`.
 *
 * Most of the #353–#430 gaps were actually closed by adding real React *pages*
 * (`/announcements/*`, landing `/mobile`), not redirects — those show
 * up here only in the coverage list (`LINKED_LEGACY_PATHS`) as `client-route`,
 * so the guard proves they still resolve. The genuine client *redirects* are
 * just the authenticated `/unsubscribe`→`/profile` alias and the two catchalls.
 */

/** A legacy path the React client redirects to a canonical React target. */
export interface RouteAlias {
  /** Legacy path other surfaces link to (router-relative, no `/react` prefix). */
  from: string;
  /** Canonical React route it redirects to. Must be a concrete route path. */
  to: string;
  /**
   * Preserve the incoming query string across the redirect (matches AngularJS
   * `redirectTo` which kept `$location.search()`), e.g. the
   * `/unsubscribe?lang=…&u=e` params from email footers (#354).
   */
  preserveSearch?: boolean;
  /** Issue that surfaced the gap, for traceability. */
  issue?: string;
  note?: string;
}

/**
 * Authenticated data-router aliases (`AuthenticatedApp.tsx`, basename `/react`).
 * Consumed there as `<Route path={from} element={<Navigate to={to} …/>}>`.
 */
export const AUTHENTICATED_ALIASES: RouteAlias[] = [
  { from: '/unsubscribe', to: '/profile', preserveSearch: true, issue: '#354', note: 'email unsubscribe links land on the profile page for logged-in users' },
  { from: '/user/features', to: '/welcome', preserveSearch: true, issue: '#993', note: 'legacy post-signup features showcase, reworked into the /welcome onboarding fork' },
];

/**
 * Authenticated catchall (#353): the legacy AngularJS `$routeProvider.otherwise`
 * client-redirects any unknown path — and bare `/` — to the schedule. The `*`
 * route in `AuthenticatedApp.tsx` navigates here (also stops the data router
 * from surfacing its default 404 UI for `/react/` or `/react/foo`).
 */
export const AUTHENTICATED_CATCHALL = '/schedule';

/**
 * Public landing router aliases (`unauthenticated/App.tsx`): several historical
 * marketing entry paths all render the same `<Welcome>` home page. Consumed
 * there by mapping each to a `<Route element={<Welcome/>}>`. These are true
 * aliases (one component, many paths), not redirects — the URL is left as-is.
 * `''` is the home path; the rest are campaign URLs kept alive for old links.
 */
export const LANDING_WELCOME_ALIASES = [
  '', // the classic home page
  'landing', // used for facebook marketing
  'app', // used in the May 2019 english video
  'promo', // used for the jdekrec flyers and distribution to arenas
  'sports', // georges laraque on twitter, instagram, facebook
  'sport', // georges laraque on twitter, instagram, facebook
];

/**
 * The landing router's `*` catchall renders `<AutoLogin>` (attempts an SSO
 * hop / otherwise routes on to signin). It is a component fallback, not a
 * path alias, so it is documented here but not encoded as data.
 */

/**
 * CONCRETE authenticated route paths served by `AuthenticatedApp.tsx`
 * (excludes the generated aliases above and the `*` catchall). This is the
 * discoverable inventory: `redirects.test.ts` cross-checks it against the
 * literal `path="…"` in the source file, so a route added to the JSX without a
 * matching entry here (or vice-versa) fails the build. Alias/catchall targets
 * are validated to resolve against this set.
 */
export const AUTHENTICATED_ROUTE_PATHS: string[] = [
  '/schedule',
  '/game/:gameId',
  '/sub/:gameId',
  '/messages',
  '/msg/:messageId',
  '/manager/message/:messageId/league/:leagueId',
  '/user/league/:leagueId/chat',
  '/user/league/:leagueId/team/:teamId/chat',
  '/user/league/:leagueId/standings',
  '/user/league/:leagueId/stats/player/:userId',
  '/admin/message/:messageId',
  '/admin/:tabname',
  '/admin',
  '/profile',
  '/announcements/update-app',
  '/announcements/2023-september-youth-leagues',
  '/promo/imagine-athletik',
  '/sendmessage/help/league/:leagueId',
  '/help',
  '/personalize',
  '/league/create',
  '/league/:leagueId',
  '/user/league/:leagueId/billing',
  '/purchase',
  '/welcome',
  '/manager/league/:leagueId',
  '/manager/league/:leagueId/customize',
];

/**
 * CONCRETE landing (public) route paths served by `unauthenticated/App.tsx`
 * (router has no basename; paths are bare, no leading slash). Includes the
 * `LANDING_WELCOME_ALIASES`. Cross-checked against the source the same way.
 * The `*` catchall (`<AutoLogin>`) is excluded.
 */
export const LANDING_ROUTE_PATHS: string[] = [
  ...LANDING_WELCOME_ALIASES,
  'features',
  'signup',
  'signin',
  'forgotPassword',
  'validateResetPassword',
  'resetPassword',
  'mobile',
  'pricing',
  'help',
  'unsubscribe',
  'deletedAccount',
];

/** Where a linked-to legacy path finds its React home. */
export type LegacyHome =
  | 'client-route' /* a concrete React route (authenticated or landing) renders it */
  | 'client-alias' /* an entry in AUTHENTICATED_ALIASES redirects it */
  | 'client-catchall' /* only the `*` catchall covers it (no dedicated route) */
  | 'server'; /* backend-owned (ViewsController); not this registry's concern */

/** A legacy URL some non-React surface still links to, and its React home. */
export interface LinkedLegacyPath {
  /** Path as linked (router-relative; `/mobile` etc. Landing paths keep their
   *  leading slash here for readability — resolution strips it). */
  path: string;
  home: LegacyHome;
  issue?: string;
  note?: string;
}

/**
 * Build-time coverage guard: every legacy URL that a non-React surface (email
 * footer, mobile-app menu, legacy jade CTA) still points at. `redirects.test.ts`
 * asserts each one resolves to the React home it claims — so a future added
 * link with no home fails the build instead of shipping as a broken URL that QA
 * has to find (the #353–#430 pattern). Add a row when a surface starts linking
 * a new legacy path; the test tells you if it has nowhere to land.
 */
export const LINKED_LEGACY_PATHS: LinkedLegacyPath[] = [
  { path: '/', home: 'client-catchall', issue: '#353', note: 'authenticated bare / -> catchall -> /schedule; landing / -> Welcome' },
  { path: '/unsubscribe', home: 'client-alias', issue: '#354', note: 'authenticated: redirect to /profile; landing: dedicated Unsubscribe page' },
  { path: '/announcements/update-app', home: 'client-route', issue: '#355', note: 'linked by the mobile-app menu (MobileAppMenuService)' },
  { path: '/announcements/2023-september-youth-leagues', home: 'client-route', issue: '#355' },
  { path: '/mobile', home: 'client-route', issue: '#430', note: 'landing page promoting the mobile app' },
  { path: '/user/features', home: 'client-alias', issue: '#993', note: 'legacy AngularJS route + old bookmarks; reworked into /welcome' },
];

/** Normalize a linked path for resolution: strip the leading slash so it can be
 *  matched against either the authenticated (`/foo`) or landing (`foo`) sets. */
function normalize(path: string): string {
  return path.replace(/^\//, '');
}

/** True if `path` is served by a concrete React route in either app. */
export function isConcreteRoute(path: string): boolean {
  const bare = normalize(path);
  return AUTHENTICATED_ROUTE_PATHS.some((p) => normalize(p) === bare) || LANDING_ROUTE_PATHS.some((p) => normalize(p) === bare);
}

/** True if `path` is a client alias `from`. */
export function isAlias(path: string): boolean {
  return AUTHENTICATED_ALIASES.some((a) => a.from === path);
}
